import { skipToken, useQuery } from "@apollo/client/react";
import { createUserAssociation } from "deso-protocol";
import { use, useCallback, useEffect, useMemo, useState } from "react";

import type { USER_PROFILE_TAB_ITEMS } from "@/components/app/feed/feed-user-wrapper";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { UserAssociationsDocument } from "@/graphql/codegen/graphql";
import { AssociationType } from "@/utils/constants";

export const usePinnedUserTabLogic = (publicKey?: string) => {
  const { currentUserPublicKey } = use(UserContext);
  const [savingPinnedTab, setSavingPinnedTab] = useState(false);
  const [pinnedTabOverride, setPinnedTabOverride] =
    useState<USER_PROFILE_TAB_ITEMS | null>(null);
  const {
    data,
    loading: loadingPinnedTab,
    refetch,
  } = useQuery(
    UserAssociationsDocument,
    publicKey
      ? {
          fetchPolicy: "cache-and-network",
          variables: {
            filter: {
              associationType: {
                equalTo: AssociationType.pinnedTabAssociationType,
              },
              target: {
                publicKey: {
                  equalTo: publicKey,
                },
              },
              transactor: {
                publicKey: {
                  equalTo: publicKey,
                },
              },
            },
          },
        }
      : skipToken,
  );

  useEffect(() => {
    setPinnedTabOverride(null);
  }, [publicKey]);

  const pinnedTabFromServer = useMemo(
    () =>
      (data?.userAssociations?.nodes[0]?.extraData
        .tab as USER_PROFILE_TAB_ITEMS) ?? null,
    [data],
  );
  const pinnedTab = pinnedTabOverride ?? pinnedTabFromServer;
  const pinnedTabLoading = loadingPinnedTab || savingPinnedTab;

  const setPinnedTabRequest = useCallback(
    async (tab: USER_PROFILE_TAB_ITEMS) => {
      if (pinnedTabLoading || !currentUserPublicKey) return;

      setSavingPinnedTab(true);

      try {
        await createUserAssociation({
          TransactorPublicKeyBase58Check: currentUserPublicKey,
          TargetUserPublicKeyBase58Check: currentUserPublicKey,
          AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
          AssociationType: AssociationType.pinnedTabAssociationType,
          AssociationValue: AssociationType.pinnedTabAssociationType,
          ExtraData: {
            tab,
          },
        });
        toast({
          title: "Success",
          description: `Tab has been pinned successfully`,
          variant: "success",
        });
        setPinnedTabOverride(tab);
        void refetch();
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened when pinning tab`,
          details: createToastErrorDetails(error),
        });
      } finally {
        setSavingPinnedTab(false);
      }
    },
    [currentUserPublicKey, pinnedTabLoading, refetch],
  );

  return { setPinnedTabRequest, pinnedTabLoading, pinnedTab };
};
